import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { IOrganizationUnitActivityRoles, IOrganizationUnitActivityAssignment } from 'typings/api-activity';
import { DoenkidsStaticValuesHelper } from 'src/components/shared/static-values/doenkids-static-values-helper';
import { ActivityService } from 'src/api/activity/activity/activity.service';
import { get } from 'lodash';
import {
  BrowseOrganizationUnitDialogComponent, ISelectedOrganizationUnit,
} from 'src/components/dialogs/browse-organization-unit-dialog/browse-organization-unit-dialog.component';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from 'src/app/utils/translate.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

interface IActivityOwner {
  id: number;
  name: string;
  type: number;
  typeDisplay?: string;
}

@Component({
    selector: 'app-publish-activity-dialog',
    templateUrl: './publish-activity-dialog.component.html',
    styleUrls: ['./publish-activity-dialog.component.scss'],
    standalone: false
})
export class PublishActivityDialogComponent {
  public selectedTargets: ISelectedOrganizationUnit[] = [];

  public originalOwner: IActivityOwner;

  public mode: 'publish' | 'share' = 'publish';

  private availableRoles: IOrganizationUnitActivityRoles[] = [];

  constructor(
    public dialogRef: MatDialogRef<PublishActivityDialogComponent>,
    public matDialog: MatDialog,
    public activityService: ActivityService,
    private readonly $translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { roles: IOrganizationUnitActivityRoles[]; mode?: 'publish' | 'share' },
  ) {
    this.availableRoles = this.data?.roles ?? [];
    this.mode = this.data?.mode ?? 'publish';
    const ownerRole = this.data?.roles?.find((role) => role.organization_unit_role_id === DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE);
    if (ownerRole) {
      this.originalOwner = {
        id: ownerRole.organization_unit_id,
        name: ownerRole.name,
        type: ownerRole.organization_unit_type_id,
      };
    }
  }

  async selectOther() {
    const alreadyAssignedIds = this.availableRoles.map((role) => role.organization_unit_id);

    const dialogRef = this.matDialog.open(BrowseOrganizationUnitDialogComponent, {
      data: {
        title: this.$translateService.instant(_('publish_activity_dialog.other.select.dialog.title')),
        description: this.$translateService.instant(_('publish_activity_dialog.other.select.dialog.description')),
        allowMultipleSelection: true,
        initialOrganizationUnitSelection: this.selectedTargets,
        disabledOrganizationUnitIds: alreadyAssignedIds,
        writeableOnly: true,
        minSelectedOus: 1,
      },
    });

    const selection: ISelectedOrganizationUnit[] = await firstValueFrom(dialogRef.afterClosed());
    if (selection && selection.length > 0) {
      this.selectedTargets = selection;
    }
  }

  removeTarget(id: number) {
    this.selectedTargets = this.selectedTargets.filter((target) => target.id !== id);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onConfirm() {
    if (this.selectedTargets.length === 0) {
      this.dialogRef.close('confirm');
      return;
    }

    const assignments: IOrganizationUnitActivityAssignment[] = this.availableRoles.map((role) => ({
      activity_id: role.activity_id,
      organization_unit_id: role.organization_unit_id,
      organization_unit_role_id: role.organization_unit_role_id,
    }));

    const originalAssignment = assignments.find((assignment) => assignment.organization_unit_id === this.originalOwner.id);

    if (originalAssignment) {
      originalAssignment.organization_unit_role_id = DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE;
    }

    for (const target of this.selectedTargets) {
      const targetAssignment = assignments.find((assignment) => assignment.organization_unit_id === target.id);
      if (targetAssignment) {
        targetAssignment.organization_unit_role_id = DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE;
      } else {
        assignments.push({
          activity_id: get(assignments, '0.activity_id'),
          organization_unit_id: target.id,
          organization_unit_role_id: DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE,
        });
      }
    }

    await firstValueFrom(this.activityService.updateOrganizationUnits(get(assignments, '0.activity_id'), { assignment: assignments }));
    this.dialogRef.close('confirm');
  }
}
